import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6 Vendor-Port Wave 1 — UV-C selector data layer (§33.6).
 *
 * UVC rating is SERVER-SIDE (no external vendor, no /rating/ dispatch): a
 * PATCH ahu/uvcs/<id> with `rate:true` makes the backend compute the rated
 * outputs in-place and echo them back on the SAME model. There are three
 * write families here, ALL change-gated + userArmed by the selector (NEVER
 * auto-fired — see UvcSelector.tsx data-safety notes); on dev3 every UVC is
 * rate=false so the rate path is unreachable read-only (parked to throwaway-AHU
 * QA per the prompt).
 *
 *  1. RATE   — PATCH ahu/uvcs/<id>  {…~25 master_*_id/series/qty/target fields,
 *              rate:true}            (server computes; response carries rows /
 *              qtys_models / notes / lamps_per_row). Legacy:
 *              self.model.save({…, rate:true}, {patch:true}).
 *  2. SELECT — PATCH ahu/uvcs/<id>  models_field (the ~30 selected fields), then
 *              a SECOND PATCH ahu/uvcs/<id> {params_field} (the catalogue id
 *              blob). Legacy selectUVC: current_model.save(models_field,{patch})
 *              .always(qty loops) then current_model.save({params_field},{patch}).
 *  3. QTY    — per-row actual-qty PATCH ahu/uvc/{power_supply,monitor,lamp}_
 *              actual_qty/<uvcId>/<catalogId>/  {qty}. Legacy: fetch the row
 *              model then save({qty},{patch}). We PATCH directly (the GET is a
 *              legacy Backbone fetch-before-save no-op; the route accepts PATCH).
 *
 * READS: the catalog collections + CMS params + the uvc detail reuse the
 * shared component-form GETs (getMasterListByUrl / getComponentDetail). Only
 * the WRITES live here.
 */

/** Loose UVC row (the retrieve serializer exposes ~60 fields). */
export type IUvcRow = Record<string, unknown>;

export type UvcActualQtyKind = 'power_supply' | 'monitor' | 'lamp';

export const uvcSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- 1. RATE (server-side compute) ----------------------------------
    // body = ~25 master_*/series/qty/target fields + rate:true. Response is the
    // full re-rated model (rows / *_qtys_model / notes / lamps_per_row).
    rateUvc: builder.mutation<IUvcRow, { id: number; data: IUvcRow }>({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/uvcs/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),

    // --- 2a. SELECT models_field ----------------------------------------
    patchUvcModelsField: builder.mutation<IUvcRow, { id: number; data: IUvcRow }>({
      query: ({ id, data }) => {
        const body = { ...data };
        delete (body as { id?: unknown }).id;
        return {
          url: `/api/v1.0/ahu/uvcs/${id}`,
          method: 'PATCH',
          body,
        };
      },
    }),

    // --- 2b. SELECT params_field (the catalogue id blob) ----------------
    patchUvcParamsField: builder.mutation<
      IUvcRow,
      { id: number; params: Record<string, unknown> }
    >({
      query: ({ id, params }) => ({
        url: `/api/v1.0/ahu/uvcs/${id}`,
        method: 'PATCH',
        body: { params_field: params },
      }),
    }),

    // --- 3. per-row actual-qty PATCH ------------------------------------
    // uvc/{kind}_actual_qty/<uvcId>/<catalogId>/  {qty}
    patchUvcActualQty: builder.mutation<
      Record<string, unknown>,
      { kind: UvcActualQtyKind; uvcId: number; catalogId: number; qty: number | string }
    >({
      query: ({ kind, uvcId, catalogId, qty }) => ({
        url: `/api/v1.0/ahu/uvc/${kind}_actual_qty/${uvcId}/${catalogId}/`,
        method: 'PATCH',
        body: { qty },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useRateUvcMutation,
  usePatchUvcModelsFieldMutation,
  usePatchUvcParamsFieldMutation,
  usePatchUvcActualQtyMutation,
} = uvcSelectorApi;
